import { useEffect, useRef } from 'preact/hooks';
import { outputItems, previewDoc, previewKind, receiveSandboxMessage, resetOutput, runFailed, runSequence, runLabel, runStatus } from '../run/output';
import { isSandboxMessage, SANDBOX_POLICY } from '../run/sandbox';
import { dockPreview, focusPreviewWindow, previewUndocked, undockPreview } from '../run/preview-window';
import { fixWithAi, runActiveCode, stopActiveRun } from '../state/actions';
import { activeCard, showToast, streaming, ui, updateUi } from '../state/store';
import { canRun, runLabelFor } from '../run/runner';
import { IconFix, IconPlay, IconPopOut, IconStop, IconTrash, IconX } from './Icons';

function popOut(): void {
  if (!undockPreview()) {
    showToast('The browser blocked the preview window. Allow pop-ups for Cue and try again.', 'error');
    return;
  }
  // Nothing previewed yet: the window should not open empty.
  if (previewDoc.peek() === null) void runActiveCode();
}

export function OutputPanel() {
  const items = outputItems.value;
  const status = runStatus.value;
  const label = runLabel.value;
  const doc = previewDoc.value;
  const kind = previewKind.value;
  const hasError = runFailed.value;
  const undocked = previewUndocked.value;
  const busy = streaming.value !== null;
  const language = activeCard.value?.language ?? 'text';
  const html = language === 'html';
  const listRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [items]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (!frameRef.current || event.source !== frameRef.current.contentWindow) return;
      if (isSandboxMessage(event.data)) receiveSandboxMessage(event.data);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  const startResize = (event: PointerEvent) => {
    const panel = panelRef.current;
    if (!panel) return;
    event.preventDefault();
    const handle = event.currentTarget as HTMLElement;
    handle.setPointerCapture(event.pointerId);
    const startY = event.clientY;
    const startHeight = panel.getBoundingClientRect().height;
    const move = (e: PointerEvent) => {
      const height = Math.min(window.innerHeight * 0.8, Math.max(120, startHeight + (startY - e.clientY)));
      panel.style.height = `${height}px`;
    };
    const up = (e: PointerEvent) => {
      handle.releasePointerCapture(e.pointerId);
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', up);
      updateUi({ outputHeight: panel.getBoundingClientRect().height });
    };
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', up);
  };

  // While the page is in its own window, the panel keeps the console and says where the page went.
  const away = undocked && html;
  const showFrame = doc !== null && !(away && kind === 'html');
  const empty = items.length === 0 && doc === null && status === 'idle' && !away;
  const statusText =
    status !== 'idle'
      ? label
      : away
        ? 'Live preview in its own window'
        : doc
          ? kind === 'html'
            ? 'Live preview'
            : 'Sandbox'
          : items.length
            ? 'Done'
            : `Press ${runLabelFor(language)} to see output here`;

  return (
    <div
      class={`output-panel${empty ? ' is-empty' : ''}`}
      ref={panelRef}
      style={empty ? undefined : { height: `${ui.value.outputHeight}px` }}
    >
      <div class="output-resize" role="separator" aria-orientation="horizontal" aria-label="Resize output" onPointerDown={startResize} />
      <div class="output-header">
        <strong>Output</strong>
        <span class={`output-status${status !== 'idle' ? ' busy' : ''}`}>{statusText}</span>
        <div class="spacer" />
        {canRun(language) &&
          (status === 'idle' ? (
            <button type="button" class="button small output-run" onClick={() => void runActiveCode()} title="Run the editor contents again">
              <IconPlay size={14} /> {runLabelFor(language)}
            </button>
          ) : (
            <button type="button" class="button small danger output-run" onClick={stopActiveRun} title="Stop the run">
              <IconStop size={14} /> Stop
            </button>
          ))}
        {hasError && (
          <button type="button" class="button small primary" onClick={() => void fixWithAi()} disabled={busy} title="Send the error to the assistant">
            <IconFix size={15} /> Fix with AI
          </button>
        )}
        {html &&
          (away ? (
            <button type="button" class="button small" onClick={dockPreview} title="Close the preview window and show the page here again">
              Bring it back
            </button>
          ) : (
            <button type="button" class="icon-button pop-out" onClick={popOut} title="Open the preview in its own window" aria-label="Open the preview in its own window">
              <IconPopOut size={16} />
            </button>
          ))}
        <button type="button" class="icon-button" title="Clear output" aria-label="Clear output" onClick={resetOutput}>
          <IconTrash size={16} />
        </button>
        <button type="button" class="icon-button" title="Hide output" aria-label="Hide output" onClick={() => updateUi({ outputOpen: false, mobileTab: 'code' })}>
          <IconX size={16} />
        </button>
      </div>
      {!empty && (
        <div class={`output-body${(showFrame && kind === 'html') || away ? ' with-preview' : ''}${items.length ? '' : ' quiet'}`}>
          {showFrame && (
            <iframe
              ref={frameRef}
              key={runSequence.value}
              class={`preview-frame${kind === 'html' ? '' : ' hidden-frame'}`}
              title="Preview"
              sandbox={SANDBOX_POLICY}
              srcdoc={doc}
            />
          )}
          {away && (
            <div class="undocked-note">
              <p>The page is in its own window, and it follows your code as you edit.</p>
              <button type="button" class="button small" onClick={focusPreviewWindow}>
                Show the window
              </button>
            </div>
          )}
          <div class="console" ref={listRef}>
            {items.map((item, i) =>
              item.kind === 'figure' ? (
                <img key={i} class="console-figure" src={item.src} alt="Figure" />
              ) : (
                <pre key={i} class={`console-line ${item.kind}`}>
                  {item.text}
                </pre>
              ),
            )}
          </div>
        </div>
      )}
    </div>
  );
}
